import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, ArrowLeft, Plus } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import api from '../../utils/api';
import FreeDeliveryCelebration, { useFreeDeliveryUnlock } from './FreeDeliveryCelebration';
import FreeGiftCelebration, { useGiftUnlock } from './FreeGiftCelebration';
import RemoveConfirmModal from './RemoveConfirmModal';
import { cldImg } from '../../utils/cloudinary';

export default function CartDrawer() {
  const { cart, cartTotal, freeTiers, freeShippingAbove, updateQty, removeFromCart, addToCart, drawerOpen, setDrawerOpen } = useCart();
  const { toggleWishlist } = useWishlist();
  const [removeTarget, setRemoveTarget] = useState(null);
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!drawerOpen || cart.length === 0) return;
    const productId = cart[0].product_id;
    const cartIds = cart.map(c => c.product_id);
    api.get(`/products/${productId}/relations`).then(({ data }) => {
      const rel = data.relations || data || {};
      const items = [
        ...(rel.you_may_also_like || []),
        ...(rel.recommendation || []),
        ...(rel.related || []),
      ].filter(p => !cartIds.includes(p.id) && p.type !== 'custom').slice(0, 5);
      if (items.length > 0) {
        setRecommendations(items);
      } else {
        api.get('/products', { params: { limit: 10 } }).then(r => {
          setRecommendations((r.data.products || []).filter(p => !cartIds.includes(p.id) && p.type !== 'custom').slice(0, 5));
        }).catch(() => {});
      }
    }).catch(() => {
      const cartIds2 = cart.map(c => c.product_id);
      api.get('/products', { params: { limit: 10 } }).then(r => {
        setRecommendations((r.data.products || []).filter(p => !cartIds2.includes(p.id) && p.type !== 'custom').slice(0, 5));
      }).catch(() => {});
    });
  }, [drawerOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setDrawerOpen]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const [celebrate, closeCelebration] = useFreeDeliveryUnlock(cartTotal, freeShippingAbove);
  const [celebrateGift, closeGiftCelebration] = useGiftUnlock(cartTotal, freeTiers);

  if (!drawerOpen) return null;

  const remaining = freeShippingAbove ? Math.max(0, freeShippingAbove - cartTotal) : 0;
  const pct = freeShippingAbove ? Math.min(100, (cartTotal / freeShippingAbove) * 100) : 0;

  // Total savings
  const totalMRP = cart.reduce((sum, item) => sum + (item.mrp || item.price) * item.qty, 0);
  const totalSavings = totalMRP - cartTotal;

  const handleGoToCart = () => { setDrawerOpen(false); navigate('/cart'); };

  return (
    <>
      {celebrate && <FreeDeliveryCelebration onClose={closeCelebration} />}
      {celebrateGift && <FreeGiftCelebration gift={celebrateGift} onClose={closeGiftCelebration} />}
      {removeTarget && (
        <RemoveConfirmModal
          item={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemove={() => { removeFromCart(removeTarget.key); setRemoveTarget(null); }}
          onMoveToWishlist={() => {
            toggleWishlist(removeTarget.product_id);
            removeFromCart(removeTarget.key);
            setRemoveTarget(null);
          }}
        />
      )}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setDrawerOpen(false)} />

      <div className="fixed top-0 right-0 h-full w-full max-w-[580px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100 shrink-0">
          <button onClick={() => setDrawerOpen(false)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h2 className="font-bold text-gray-800 text-base">
            My Cart ({cart.length} {cart.length === 1 ? 'item' : 'items'})
          </h2>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
            <p className="text-gray-400 text-sm">Your cart is empty</p>
            <button onClick={() => setDrawerOpen(false)} className="text-sm font-bold text-brand-yellow hover:underline">
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto">

              {/* Savings banner */}
              {totalSavings > 0 && (
                <div className="bg-teal-50 border-b border-teal-100 px-4 py-2.5 text-center">
                  <p className="text-sm font-bold text-teal-700">
                    You are saving ₹{totalSavings.toLocaleString('en-IN')} today!
                  </p>
                </div>
              )}

              {/* Combined progress bar — free shipping + gift tiers */}
              {(freeTiers?.length > 0 || (freeShippingAbove && freeShippingAbove > 0)) && (() => {
                const giftStations = (freeTiers || []).map(t => ({
                  type: 'gift',
                  amount: parseFloat(t.min_amount),
                  label: t.product?.name?.split(' ').slice(0, 2).join(' '),
                  img: t.product?.images?.[0]?.image_url,
                }));
                const shippingStation = freeShippingAbove && freeShippingAbove > 0
                  ? [{ type: 'shipping', amount: freeShippingAbove, label: 'Free Delivery', img: null }]
                  : [];
                const allStations = [...giftStations, ...shippingStation].sort((a, b) => a.amount - b.amount);
                if (allStations.length === 0) return null;

                const DOT = 52;
                const HALF = DOT / 2;
                const n = allStations.length;

                // Status message
                const nextStation = allStations.find(s => cartTotal < s.amount);
                const lastUnlocked = [...allStations].reverse().find(s => cartTotal >= s.amount);

                // Evenly space dots; compute fill to match
                const lastIdx = lastUnlocked ? allStations.indexOf(lastUnlocked) : -1;
                const nextIdx = nextStation ? allStations.indexOf(nextStation) : n;
                let fillPct = 0;
                if (lastIdx >= 0) {
                  const prevRatio = n === 1 ? 1 : lastIdx / (n - 1);
                  const nextRatio = nextIdx < n ? (n === 1 ? 1 : nextIdx / (n - 1)) : 1;
                  const prevAmt = allStations[lastIdx].amount;
                  const nextAmt = nextIdx < n ? allStations[nextIdx].amount : allStations[lastIdx].amount;
                  const segFrac = nextAmt > prevAmt ? Math.min(1, (cartTotal - prevAmt) / (nextAmt - prevAmt)) : 1;
                  fillPct = Math.min(100, (prevRatio + segFrac * (nextRatio - prevRatio)) * 100);
                }

                return (
                  <div className="border-b px-4 py-3" style={{ backgroundColor: '#fdf6e3', borderColor: '#e8d9b0' }}>
                    {lastUnlocked && (
                      <p className="text-xs font-bold mb-1.5" style={{ color: '#92400e' }}>
                        {lastUnlocked.type === 'shipping' ? '🚚' : '🎁'}{' '}
                        <span className="underline">{lastUnlocked.label}</span> unlocked!
                        {nextStation && (
                          <span className="font-normal" style={{ color: '#78350f' }}>
                            {' '}Add ₹{(nextStation.amount - cartTotal).toLocaleString('en-IN')} more for{' '}
                            {nextStation.type === 'shipping' ? 'Free Delivery' : nextStation.label}
                          </span>
                        )}
                      </p>
                    )}
                    {!lastUnlocked && nextStation && (
                      <p className="text-xs font-semibold mb-1.5" style={{ color: '#78350f' }}>
                        {nextStation.type === 'shipping' ? '🚚' : '🎁'}{' '}
                        Add ₹{(nextStation.amount - cartTotal).toLocaleString('en-IN')} more to get{' '}
                        <span className="font-bold" style={{ color: '#92400e' }}>{nextStation.type === 'shipping' ? 'Free Delivery' : nextStation.label}</span>
                      </p>
                    )}

                    <div className="relative mt-3" style={{ paddingLeft: HALF, paddingRight: HALF, paddingTop: 16, paddingBottom: 44 }}>
                      <div className="relative w-full rounded-full" style={{ height: 8, marginTop: HALF - 4, backgroundColor: '#e5d8b0' }}>
                        <div className="absolute left-0 top-0 h-full rounded-full transition-all duration-500" style={{ width: `${fillPct}%`, backgroundColor: '#F5B400' }} />
                      </div>
                      {allStations.map((s, i) => {
                        const ratio = n === 1 ? 1 : i / (n - 1);
                        const isActive = lastUnlocked && s.amount === lastUnlocked.amount;
                        const isPassed = cartTotal >= s.amount && !isActive;
                        return (
                          <div key={i} className="absolute flex flex-col items-center"
                            style={{ left: `calc(${HALF}px + ${ratio * 100}% * (100% - ${DOT}px) / 100%)`, transform: 'translateX(-50%)', top: 0, width: 64 }}>
                            <span className="text-[9px] font-bold whitespace-nowrap mb-1" style={{ color: isActive || isPassed ? '#92400e' : '#a78040' }}>
                              ₹{s.amount.toLocaleString('en-IN')}
                            </span>
                            <div className="rounded-full flex items-center justify-center transition-all overflow-hidden"
                              style={{
                                width: DOT, height: DOT,
                                border: isActive ? '3px solid #F5B400' : isPassed ? '3px solid #F5B400' : '2px solid #d4b96a',
                                backgroundColor: '#fff',
                                boxShadow: isActive ? '0 2px 10px #F5B40055' : 'none',
                                opacity: isActive || isPassed ? 1 : 0.75,
                              }}>
                              {s.type === 'shipping'
                                ? <span className="text-2xl">🚚</span>
                                : s.img
                                  ? <img src={cldImg(s.img, 160)} alt={s.label} className="w-full h-full object-cover" />
                                  : <span className="text-xl">🎁</span>}
                            </div>
                            <span className="text-[8px] font-bold text-center leading-tight mt-1" style={{ maxWidth: 64, display: 'block', color: isActive || isPassed ? '#111' : '#7a6030', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {s.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Cart items */}
              <div className="divide-y divide-gray-100">
                {cart.map(item => {
                  const itemMRP = item.mrp || item.price;
                  const discountPct = itemMRP > item.price ? Math.round(((itemMRP - item.price) / itemMRP) * 100) : null;

                  // Free gift item — special render
                  if (item.isFreeGift) {
                    return (
                      <div key={item.key} className="px-4 py-3 bg-orange-50">
                        <div className="flex gap-3 items-center">
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white border border-orange-200 shrink-0">
                            {item.image
                              ? <img src={cldImg(item.image, 160)} alt={item.name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-xl">🎁</div>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 line-clamp-1">{item.name}</p>
                            {item.variant_label && <p className="text-xs text-gray-500">{item.variant_label}</p>}
                            <span className="inline-block mt-1 text-[10px] font-bold bg-orange-400 text-white px-2 py-0.5 rounded-full">
                              🎁 FREE GIFT
                            </span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-sm font-bold text-orange-600">FREE</span>
                            {item.mrp > 0 && (
                              <span className="text-xs text-gray-400 line-through block">₹{item.mrp.toLocaleString('en-IN')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.key} className="px-4 py-4">
                      <div className="flex gap-3">
                        {/* Image */}
                        <Link to={`/product/${item.slug}`} onClick={() => setDrawerOpen(false)}
                          className="w-28 h-28 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0">
                          {item.image
                            ? <img src={cldImg(item.image, 160)} alt={item.name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-2xl">📦</div>}
                        </Link>

                        {/* Middle: name + variant */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">{item.name}</p>
                          {item.variant_label && (
                            <p className="text-xs text-gray-500 mt-0.5">{item.variant_label}</p>
                          )}
                          {itemMRP > item.price && (
                            <p className="text-[11px] font-bold text-green-600 mt-1">
                              You save ₹{((itemMRP - item.price) * item.qty).toLocaleString('en-IN')} ({discountPct}% off)
                            </p>
                          )}
                        </div>

                        {/* Right: X + price + qty */}
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <button onClick={() => setRemoveTarget(item)}
                            className="text-gray-400 hover:text-gray-600 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                          <div className="text-right">
                            <span className="text-base font-bold text-gray-900 block leading-tight">
                              ₹{(item.price * item.qty).toLocaleString('en-IN')}
                            </span>
                            {itemMRP > item.price && (
                              <span className="text-xs text-gray-400 line-through block mt-0.5">
                                ₹{(itemMRP * item.qty).toLocaleString('en-IN')}
                              </span>
                            )}
                            {discountPct && (
                              <span className="text-[11px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full inline-block mt-1">
                                {discountPct}% OFF
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-0 border border-gray-200 rounded-full overflow-hidden">
                            <button onClick={() => item.qty === 1 ? setRemoveTarget(item) : updateQty(item.key, item.qty - 1)}
                              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 font-bold text-lg">
                              −
                            </button>
                            <span className="w-7 text-center text-sm font-bold text-gray-800">{item.qty}</span>
                            <button onClick={() => updateQty(item.key, item.qty + 1)}
                              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 font-bold text-lg">
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recommendations */}
              {recommendations.filter(p => !cart.map(c => c.product_id).includes(p.id)).length > 0 && (
                <div className="px-4 py-4 border-t border-gray-100">
                  <p className="text-xs font-bold text-gray-600 mb-3">Recommended for you</p>
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {recommendations.filter(p => !cart.map(c => c.product_id).includes(p.id)).map(p => {
                      const thumb = p.images?.[0]?.image_url;
                      const v = p.variants?.[0];
                      const price = parseFloat(v?.discount_price) || parseFloat(v?.price) || 0;
                      const mrp = parseFloat(v?.price) || 0;
                      const disc = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : null;
                      return (
                        <div key={p.id} className="flex-shrink-0 w-28 group">
                          <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 mb-1.5">
                            <Link to={`/product/${p.slug}`} onClick={() => setDrawerOpen(false)}>
                              {thumb
                                ? <img src={cldImg(thumb, 160)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                : <div className="w-full h-full flex items-center justify-center text-xl">📦</div>}
                            </Link>
                            <button
                              onClick={() => addToCart(p, v, 1)}
                              className="absolute bottom-1.5 right-1.5 w-6 h-6 bg-brand-yellow rounded-full flex items-center justify-center shadow hover:opacity-90 active:scale-95 transition-all">
                              <Plus className="w-3.5 h-3.5 text-brand-dark" />
                            </button>
                          </div>
                          <p className="text-[11px] font-semibold text-gray-800 line-clamp-2 leading-tight">{p.name}</p>
                          {price > 0 && (
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              <span className="text-[11px] font-black text-gray-800">₹{price.toLocaleString('en-IN')}</span>
                              {disc && <span className="text-[9px] font-bold text-teal-600 bg-teal-50 px-1 py-0.5 rounded-full">{disc}% OFF</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-4 py-4 shrink-0">
              <button onClick={handleGoToCart}
                className="w-full bg-brand-yellow text-brand-dark font-bold py-4 rounded-xl text-sm hover:opacity-90 transition-opacity">
                Go to Cart →
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
